import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { App, Button, DatePicker, Select, Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import BrandButton from '@/components/BrandButton'
import {
  SETTLEMENT_BILLING_TYPES,
  SETTLEMENT_STATUSES,
  getAvailableBalance,
  listSettlementStatements,
  type SettlementBillingType,
  type SettlementStatement,
  type SettlementStatus,
} from '@/api/settlements'
import { useTranslation } from 'react-i18next'
import SettlementsWithdrawModal from './WithdrawModal'
import {
  FilterAll,
  SETTLEMENT_BILLING_I18N,
  SETTLEMENT_STATUS_I18N,
  filterStatements,
  formatCny,
  isServiceOngoing,
} from './format'

function Settlements() {
  const { message } = App.useApp()
  const { t } = useTranslation()
  const { app_id = '' } = useParams()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const [balance, setBalance] = useState<number | null>(null)
  const [rows, setRows] = useState<SettlementStatement[]>([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const lastClick = useRef(0)

  const filters = {
    status: (params.get('status') ?? '') as SettlementStatus | '',
    billing: (params.get('billing') ?? '') as SettlementBillingType | '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
  }

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const loadBalance = async () => {
    try {
      setBalance(await getAvailableBalance())
    } catch {
      message.error(t('settlements.balanceFail'))
    }
  }

  const loadList = async () => {
    setLoading(true)
    try {
      setRows(await listSettlementStatements())
    } catch {
      message.error(t('settlements.loadFail'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadBalance()
    void loadList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = useMemo(() => filterStatements(rows, filters), [rows, filters])

  const goDetail = (id: string) => {
    navigate(`/apps/${app_id}/account/settlements/statements/${id}?${params.toString()}`)
  }

  const copy = async (text: string) => {
    try {
      await navigator.clipboard?.writeText(text)
      message.success(t('settlements.copied'))
    } catch {
      message.error(t('settlements.loadFail'))
    }
  }

  const onWithdraw = () => {
    const now = Date.now()
    if (now - lastClick.current < 1000) return
    lastClick.current = now
    if ((balance ?? 0) <= 0) {
      message.error(t('settlements.insufficient'))
      return
    }
    setOpen(true)
  }

  const columns: ColumnsType<SettlementStatement> = [
    {
      title: t('settlements.statementNo'),
      dataIndex: 'statement_no',
      render: (no: string, row) => (
        <Button type="link" className="px-0" onClick={() => goDetail(row.id)}>
          <span onDoubleClick={() => void copy(no)}>{no}</span>
        </Button>
      ),
    },
    {
      title: t('settlements.orderNo'),
      dataIndex: 'order_no',
      render: (no: string) => (
        <Button type="link" className="px-0" onClick={() => message.error(t('settlements.noPermOrder'))}>
          {no}
        </Button>
      ),
    },
    {
      title: t('settlements.appName'),
      dataIndex: 'app_name',
      ellipsis: { showTitle: true },
    },
    {
      title: t('settlements.productName'),
      dataIndex: 'product_name',
      ellipsis: { showTitle: true },
    },
    {
      title: t('settlements.billing'),
      dataIndex: 'billing_type',
      render: (type: SettlementBillingType) => t(SETTLEMENT_BILLING_I18N[type]),
    },
    {
      title: t('settlements.spec'),
      dataIndex: 'spec',
      ellipsis: { showTitle: true },
    },
    {
      title: t('settlements.settlementDate'),
      dataIndex: 'settlement_date',
      render: (_, row) => (
        <span className="inline-flex items-center gap-2">
          {row.settlement_date}
          {isServiceOngoing(row.service_end) && (
            <Tooltip title={t('settlements.ongoingTip')}>
              <span className="rounded bg-fill px-1.5 text-xs text-muted">{t('settlements.ongoing')}</span>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: t('settlements.amount'),
      dataIndex: 'amount',
      render: (n: number) => formatCny(n),
    },
    {
      title: t('settlements.status'),
      dataIndex: 'status',
      render: (s: SettlementStatus) => t(SETTLEMENT_STATUS_I18N[s]),
    },
    {
      title: t('settlements.action'),
      render: (_, row) => (
        <Button type="link" className="px-0" onClick={() => goDetail(row.id)}>
          {t('settlements.detail')}
        </Button>
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-6 p-8 m-5 border border-line rounded-lg">
      <div className="flex items-start justify-between gap-4">
        <p className="m-0 max-w-xl text-sm text-secondary">
          {balance === 0 ? t('settlements.firstHint') : t('settlements.intro')}
        </p>
        <BrandButton onClick={onWithdraw}>{t('settlements.withdraw')}</BrandButton>
      </div>

      <div className="w-fit rounded-lg border border-line px-6 py-4">
        <div className="font-mono text-2xl">{formatCny(balance ?? 0, true)}</div>
        <div className="mt-1 text-xs text-muted">{t('settlements.balanceHint')}</div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium">{t('settlements.statements')}</span>
        <Select
          className="w-32"
          value={filters.status || FilterAll}
          onChange={(v) => setFilter('status', v === FilterAll ? '' : v)}
          options={[
            { value: FilterAll, label: t('settlements.all') },
            ...SETTLEMENT_STATUSES.map((status) => ({
              value: status,
              label: t(SETTLEMENT_STATUS_I18N[status]),
            })),
          ]}
        />
        <Select
          className="w-36"
          value={filters.billing || FilterAll}
          onChange={(v) => setFilter('billing', v === FilterAll ? '' : v)}
          options={[
            { value: FilterAll, label: t('settlements.all') },
            ...SETTLEMENT_BILLING_TYPES.map((type) => ({
              value: type,
              label: t(SETTLEMENT_BILLING_I18N[type]),
            })),
          ]}
        />
        <DatePicker.RangePicker
          value={[filters.from ? dayjs(filters.from) : null, filters.to ? dayjs(filters.to) : null]}
          placeholder={[t('settlements.dateStart'), t('settlements.dateEnd')]}
          onChange={(_, dates) => {
            setFilter('from', dates[0] ?? '')
            setFilter('to', dates[1] ?? '')
          }}
        />
      </div>

      <Table
        rowKey="id"
        loading={loading}
        scroll={{ x: 1280 }}
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 20, hideOnSinglePage: filtered.length <= 20 }}
        locale={{
          emptyText: rows.length === 0 ? t('settlements.empty') : t('settlements.emptyFilter'),
        }}
      />

      <SettlementsWithdrawModal
        open={open}
        balance={balance ?? 0}
        onClose={() => setOpen(false)}
        onDone={setBalance}
      />
    </div>
  )
}

export default Settlements
